import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const Backdrop = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4, ease: "easeOut" }}
    className="login-wave-bg h-screen w-full flex flex-col items-center justify-center relative overflow-hidden"
  >
    <motion.div
      aria-hidden
      className="absolute inset-y-0 w-1/2 pointer-events-none"
      style={{
        background:
          "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
      }}
      initial={{ x: "-100%" }}
      animate={{ x: "300%" }}
      transition={{ duration: 3.5, repeat: Infinity, ease: "linear" }}
    />
    {children}
  </motion.div>
);

export const PageLoader = () => {
  const [progress, setProgress] = useState(0);
  const [isWaterVariant, setIsWaterVariant] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => setIsWaterVariant(window.innerWidth >= 768);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);
    return () => clearInterval(interval);
  }, []);

  const APP_NAME = import.meta.env.VITE_APP_NAME || "SKILL2U";

  if (isWaterVariant) {
    return (
      <Backdrop>
        <style>
          {`
            @keyframes wave {
              0%, 100% { transform: translateX(-50%) translateY(0); }
              50% { transform: translateX(-50%) translateY(10px); }
            }
          `}
        </style>

        {/* Soft breathing halo behind the logo */}
        <motion.div
          aria-hidden
          className="absolute w-[420px] h-[420px] rounded-full blur-3xl"
          style={{
            background:
              "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 60%)",
          }}
          animate={{ scale: [1, 1.1, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative w-[280px] md:w-[400px] h-[80px] overflow-hidden"
        >
          <img
            src="/logo.svg"
            alt="logo"
            className="absolute top-0 left-0 w-full h-full object-contain"
          />
          <div
            className="absolute left-0 w-[200%] -translate-x-1/2 h-[50px] overflow-hidden"
            style={{
              top: `${100 - progress}%`,
              animation: progress < 100 ? "wave 2s ease-in-out infinite" : "none",
            }}
          >
            <img
              src="/logo_b.svg"
              alt="logo fill"
              className="absolute top-0 left-0 w-1/2 h-full object-contain"
              style={{ clipPath: `inset(${100 - progress}% 0 0 0)` }}
            />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="relative w-48 h-1 bg-white/20 rounded-full mt-8 overflow-hidden"
        >
          <motion.div
            className="h-full bg-white rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1, ease: "linear" }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="mt-4 text-center"
        >
          <span className="text-white/60 text-sm">
            {progress < 100 ? "Loading..." : "Redirecting..."}
          </span>
        </motion.div>
      </Backdrop>
    );
  }

  return (
    <Backdrop>
      {/* Soft breathing halo behind the wordmark */}
      <motion.div
        aria-hidden
        className="absolute w-[320px] h-[320px] rounded-full blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 60%)",
        }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="relative">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
          className="flex items-center justify-center gap-0.5 overflow-hidden h-16"
        >
          {APP_NAME.split("").map((letter: string, index: number) => (
            <motion.span
              key={index}
              variants={{
                hidden: { opacity: 0, y: 40 },
                show: { opacity: 1, y: 0 },
              }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-5xl font-bold text-white inline-block"
            >
              {letter}
            </motion.span>
          ))}
        </motion.div>

        <div className="w-full h-1 bg-white/20 rounded-full mt-2 overflow-hidden">
          <motion.div
            className="h-full bg-white rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.1, ease: "linear" }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
          className="absolute -bottom-8 left-0 w-full text-center"
        >
          <span className="text-white/60 text-sm">
            {progress < 100 ? "Loading..." : "Redirecting..."}
          </span>
        </motion.div>
      </div>
    </Backdrop>
  );
};
